// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { addToChainBefore, Handler, HasOnSuccess, replaceInChain } from "@insite/client-framework/HandlerCreator";
import {
    GetProductCollectionApiV2Parameter,
    GetRelatedProductCollectionApiV2Parameter,
} from "@insite/client-framework/Services/ProductServiceV2";
import {
    chain,
    DispatchCompleteLoadProducts,
    PopulateApiParameter,
} from "@insite/client-framework/Store/Data/Products/Handlers/LoadProducts";
import { getProductsDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { ProductCollectionModel, ProductModel } from "@insite/client-framework/Types/ApiModels";

type Parameter = (GetProductCollectionApiV2Parameter | GetRelatedProductCollectionApiV2Parameter) &
    { isLoadMore?: boolean } & HasOnSuccess<ProductModel[]>;
type Props = {
    apiParameter: GetProductCollectionApiV2Parameter | GetRelatedProductCollectionApiV2Parameter;
    apiResult: ProductCollectionModel;
};

type HandlerType = Handler<Parameter, Props>;

/**
 *
 * @param props
 * @override clean the props in case we have includeProductsInSubCategories and there is no categoryId which will cause an API error since its
 * required to have a specific categoryId for this condition
 */
export const XngModifyParametersAfterPopulate: any = (props: any) => {
    if (
        !props.parameter?.hasOwnProperty("categoryId") &&
        props.parameter?.hasOwnProperty("includeProductsInSubCategories")
    ) {
        props.parameter.includeProductsInSubCategories = false;
    }
};

/**
 *
 * @param props
 * @override in case of loading recently view carousel on PDP, omit the PDP product from the apiResult products to avoid overwritting its
 * value on the store
 */
export const XngDispatchCompleteLoadProducts: HandlerType = props => {
    if (props.parameter.isLoadMore) {
        const prevApiParams: typeof props.apiParameter = { ...props.apiParameter };
        prevApiParams.page =
            props.apiParameter.page && props.apiParameter.page > 1 ? props.apiParameter.page - 1 : undefined;
        const prevProducts = getProductsDataView(props.getState(), prevApiParams).value;
        if (prevProducts && props.apiResult.products) {
            props.apiResult.products = [...prevProducts, ...props.apiResult.products];
        }
        if (!prevProducts && props.parameter.page && props.parameter.page > 1) {
            props.apiParameter.page = props.parameter.page;
            props.apiParameter.pageSize = props.parameter.pageSize;
        }
    }
    
    if ((props.parameter as GetProductCollectionApiV2Parameter).filter === "recentlyViewed") {
        const products = props.apiResult.products?.filter(
            product => product.id !== (props.parameter as any).excludeProductId,
        );
        const collection = { ...props.apiResult, products } as ProductCollectionModel;
        props.dispatch({
            type: "Data/Products/CompleteLoadProducts",
            collection,
            parameter: props.apiParameter,
        });
    } else {
        props.dispatch({
            type: "Data/Products/CompleteLoadProducts",
            collection: props.apiResult,
            parameter: props.apiParameter,
        });
    }
};

addToChainBefore(chain, PopulateApiParameter, XngModifyParametersAfterPopulate);
replaceInChain(chain, DispatchCompleteLoadProducts, XngDispatchCompleteLoadProducts);
